import type { GetStaticPaths, GetStaticProps, InferGetStaticPropsType } from 'next'
import type { ReactNode } from 'react'
import type { BlogPost } from '@/types'

import { Layout, Seo } from '@/components/generals'
import RichText from '@/components/generals/rich-text'
import { BlogCard } from '@/components/landing/blog-card'
import { HeroWords } from '@/components/landing/hero-words'
import { getBlogPosts, getBlogPost } from '@/lib/cms'

import { siteConfig } from '@/constants/site'

// `related` is defaulted because a client that still holds this page's PREVIOUS
// pageProps (a hot reload, or a cached __NEXT_DATA__ payload from before this
// prop existed) would otherwise render the new component against old props and
// crash on `related.length`. getStaticProps always supplies a real array.
const BlogDetailPage = ({ post, related = [] }: InferGetStaticPropsType<typeof getStaticProps>): ReactNode => {
    const shareUrl = encodeURIComponent(`${siteConfig.url}/insights/${post.slug}`)
    const shareText = encodeURIComponent(post.name)

    return (
        <Layout>
            <Seo title={post.name} description={post.summary} ogImage={post.mainImage} type='article' />

            <div className='page-wrapper'>
                <div className='hero-section-secondary no-zindex'>
                    <div className='container'>
                        <div className='hero-content'>
                            <div className='mdl-layout-grid detail-grid'>
                                <div className='content animation-on-page-load-4'>
                                    <div className='sticky-content'>
                                        <div className='detail-data-parent'>
                                            <div className='detail-data-holder'>
                                                <div className='detail-line' />
                                                <div>
                                                    <strong>Date</strong>
                                                </div>
                                                <div data-mdlfy-cms={`blog_posts:${post.id}:date`}>{post.date}</div>
                                            </div>
                                            <div className='detail-data-holder'>
                                                <div className='detail-line' />
                                                <div>
                                                    <strong>Topic</strong>
                                                </div>
                                                <div className='flex-horizontal'>
                                                    <div className='badge' data-mdlfy-cms={`blog_posts:${post.id}:topic`}>{post.topic}</div>
                                                </div>
                                            </div>
                                            <div className='detail-data-holder'>
                                                <div className='detail-line' />
                                                <div className='follow-us-main-panel'>
                                                    <div className='share-panel'>
                                                        <div className='share-text-holder'>
                                                            <div>
                                                                <strong>Share</strong>
                                                            </div>
                                                            <img className='follow-us-arrow-image' src='/images/arrow-down-right.svg' alt='' />
                                                        </div>
                                                        <div className='social-media-icons'>
                                                            <a className='social-media-link _2 mdl-inline-block' href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`} target='_blank' rel='noopener noreferrer' aria-label='Share on LinkedIn'>
                                                                <img className='social-media-icon' src='/images/linkedin.svg' alt='' />
                                                                <div className='link-background' />
                                                            </a>
                                                            <a className='social-media-link _3 mdl-inline-block' href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareText}`} target='_blank' rel='noopener noreferrer' aria-label='Share on X'>
                                                                <img className='social-media-icon' src='/images/x-twitter.svg' alt='' />
                                                                <div className='link-background' />
                                                            </a>
                                                            <a className='social-media-link _4 mdl-inline-block' href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`} target='_blank' rel='noopener noreferrer' aria-label='Share on Facebook'>
                                                                <img className='social-media-icon' src='/images/facebook.svg' alt='' />
                                                                <div className='link-background' />
                                                            </a>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className='content'>
                                    <HeroWords text={post.name} />
                                    <div className='margin-top-2em'>
                                        <div className='animation-on-page-load-2'>
                                            <div className='hero-description no-max-width' data-mdlfy-cms={`blog_posts:${post.id}:summary`}>{post.summary}</div>
                                        </div>
                                    </div>
                                    <div className='animation-on-page-load-3'>
                                        <div className='fade-in-move-on-scroll'>
                                            <img className='post-image' src={post.mainImage} loading='lazy' alt={post.mainImageAlt || post.name} />
                                            <RichText html={post.body} className='mdl-richtext' />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='section-background hero-blog-bg-section' />
                </div>

                {related.length > 0 && (
                    <div className='section overflow-hidden'>
                        <div className='container'>
                            <div className='mdl-layout-grid grid-2-col'>
                                <div className='content'>
                                    <div className='fade-in-move-on-scroll'>
                                        <h2 className='no-margins'>More Articles</h2>
                                    </div>
                                </div>
                                <div className='content'>
                                    <div className='fade-in-move-on-scroll'>
                                        <div className='margin-top-2em'>
                                            <div className='flex-horizontal'>
                                                <a className='button button-link mdl-inline-block' href='/insights'>
                                                    <div>View All</div>
                                                    <span className='button-icon' aria-hidden='true' />
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className='blog-wrapper mdl-dyn-list'>
                                <div className='blog-grid mdl-dyn-items' role='list'>
                                    {related.map((item) => (
                                        <BlogCard key={item.slug} post={item} />
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className='section-background' />
                    </div>
                )}
            </div>
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    const posts = await getBlogPosts()
    return {
        paths: posts.map((post) => ({ params: { slug: post.slug } })),
        fallback: 'blocking'
    }
}

export const getStaticProps: GetStaticProps<{ post: BlogPost; related: BlogPost[] }> = async ({ params }) => {
    const slug = params?.slug as string
    const post = await getBlogPost(slug)
    if (!post) return { notFound: true, revalidate: 60 }

    // The three next posts in listing order, wrapping around so the last post
    // still gets a full row instead of an empty one.
    const posts = await getBlogPosts()
    const index = posts.findIndex((p) => p.slug === slug)
    const others = [...posts.slice(index + 1), ...posts.slice(0, Math.max(index, 0))]
    const related = others.filter((p) => p.slug !== slug).slice(0, 3)

    return { props: { post, related }, revalidate: 60 }
}

export default BlogDetailPage
